<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LocaleCode } from '../../../utils/i18n'
import { post, put } from "@rails/request.js"

const props = defineProps<{
  user_signed_in: boolean,
  locale: LocaleCode,
  data: {
    official_url: string
    paths: {
      extract_og_image_path: string,
      update_game_thumbnail_path: string,
      update_game_thumbnail_by_url_path: string,
      update_official_url_path: string
    }
  }
}>()
const { t } = useI18n({ locale: props.locale })

type uploadMode = "opengraph" | "upload"
const selectedMode = ref<uploadMode>("opengraph")

const official_url = ref(props.data.official_url)
const image = ref<File>()
const image_url = ref<string>('');
const fileForm = ref<HTMLElement>()
const onClickUploadEmpty = () => {
  fileForm.value?.click()
}

const uploadImage = (e: Event) => {
  const target = e.target as HTMLInputElement
  if(target && target.files) {
    image.value = target.files[0]
  }
}

const onClickOpengraphEmpty = async() => {
  if(!official_url.value) { return }
  const response = await post(props.data.paths.extract_og_image_path,{
    responseKind: "json",
    body: { url: official_url.value }
  })
  if(!response.ok) { return alert(t("cannot_load_url")) }
  const json = await response.json
  if(!json.image_url) { alert(t("cannot_load_url")) }
  image_url.value = json.image_url
}

const fileSelected = computed(() => {
  return image?.value instanceof File
})

const uploadedFileSource = computed(() => {
  if (!image.value) { return }
  return URL.createObjectURL(image.value)
})

const onClickSave = async () => {
  if (selectedMode.value === "opengraph" && image_url.value) {
    const response = await put(props.data.paths.update_game_thumbnail_by_url_path, {
      responseKind: "turbo-stream",
      body: { url: image_url.value }
    })
    response.renderTurboStream();
  } else if (selectedMode.value === "upload" && image.value) {
    const formData = new FormData()
    formData.append("image", image.value)
    const response = await put(props.data.paths.update_game_thumbnail_path, {
      responseKind: "turbo-stream",
      body: formData
    })
    response.renderTurboStream();
  }
  if (official_url.value !== props.data.official_url) {
    const response = await put(props.data.paths.update_official_url_path, {
      responseKind: "turbo-stream",
      body: { official_url: official_url.value }
    })
    response.renderTurboStream();
  }
}

</script>

<template lang="pug">
button(uk-toggle="target: #thumbnail")
  | {{ t('edit') }}
div.modal__wrapper#thumbnail(uk-modal)
  div.modal__medium(class="uk-modal-dialog uk-margin-auto-vertical")
    div.modal__body(class="uk-modal-body" uk-overflow-auto)

      div.games__modal-thumbnail

        div.games__modal-thumbnail-item
          div.games__modal-thumbnail-label {{ $t('js.games.game.official_url') }}
          div.games__modal-thumbnail-value 
            input(
              class="uk-input uk-border-pill" type="text"
              v-model="official_url" placeholder="https://example.com"
              id="game_thumbnail_official_url"
            )

        div.games__modal-thumbnail-item
          div.games__modal-thumbnail-label {{ t('image') }}
          div.games__modal-thumbnail-value(class="image")
            div.games__modal-thumbnail-value-image(:class="{ 'image-selected': selectedMode === 'opengraph'}")
              div.games__modal-thumbnail-value-label
                input(type="radio" name="image" class="uk-radio" id="opengraph" value="opengraph" v-model="selectedMode")
                label(for="opengraph") {{  t('embedded_image') }}
              img.games__modal-thumbnail-value-preview(width="1200" height="630" alt="" :src="image_url" v-if="image_url")
              span.games__modal-thumbnail-value-empty(@click="onClickOpengraphEmpty" v-else) {{ t('load_from_url') }}
              span.games__modal-thumbnail-value-reload(v-if="image_url" @click="onClickOpengraphEmpty")  {{ t('reload') }}

            div.games__modal-thumbnail-value-image(:class="{ 'image-selected': selectedMode === 'upload'}")
              div.games__modal-thumbnail-value-label
                input(type="radio" name="image" class="uk-radio" id="upload_image" value="upload" v-model="selectedMode")
                label(for="upload_image") {{ t('upload_image') }}
              input.games__modal-thumbnail-value-file(type="file" accept="image/*" @change="uploadImage" ref="fileForm")
              img.games__modal-thumbnail-value-preview(width="1200" height="630" alt="" :src="uploadedFileSource" v-if="fileSelected")
              span.games__modal-thumbnail-value-empty(@click="onClickUploadEmpty" v-else) {{ t("select_file") }}
              span.games__modal-thumbnail-value-reload(v-if="fileSelected" @click="onClickUploadEmpty") {{ t("reselect") }}

    div.modal__footer(class="uk-modal-footer uk-text-center")
      button(class="uk-button uk-button-default uk-modal-close" type="button") {{ t('close') }}
      button(class="uk-button uk-button-primary uk-modal-submit" type="button" @click="onClickSave") {{ t('save') }}
</template>

<i18n lang="json">
  {
    "ja": {
      "edit": "編集", 
      "image": "サムネイル用の画像",
      "embedded_image": "URLの埋め込み画像",
      "upload_image": "画像のアップロード",
      "load_from_url": "URLから読み込む",
      "reload": "再読み込み",
      "select_file": "ファイルを選択",
      "reselect": "再選択",
      "close": "閉じる",
      "save": "保存",
      "cannot_load_url": "URLの読み込みに失敗しました"
    }
  }
</i18n>
